import {
  Button,
  Form,
  FormSubmitHandler,
  openDropdown,
  Label,
  HelperText,
  Dropdown,
  DropdownItem,
  DataStatus,
} from 'platform/components';
import {Box, Hide, HStack, Show, Spinner, Text, VStack, Grid} from 'platform/foundation';
import {z} from 'zod';

import {useDispatch} from 'react-redux';

import {isNilOrEmpty} from 'ramda-adjunct';

import {
  chatAgentApi,
  MessageChannelTypeEnum,
  TemplateContextEnum,
  useGetBusinessCaseMessageChannelQuery,
  useGetContextTemplateListQuery,
  useGetParticipationQuery,
  useGetWhatsAppConfigQuery,
  useRenderBusinessCaseSmsTemplateMutation,
  useRenderCustomerSmsTemplateMutation,
  useSendWhatsAppContentMessageMutation,
  useSetBusinessCaseMessageChannelMutation,
  useSubscribeToChatConversationMutation,
} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {useGetBusinessCaseQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError, useBcToOnlineOffer, usePermissions} from '@dms/shared';

import {GetConversationDetailResponse, sendEventAsPublicUserBody} from '@chat/shared/validations';
import {RichContentInput} from '@chat/ui';

import {MessageFormValues} from '../types/MessageFormValues';
import {Input} from './richContent/Input';
import {TemplatesChatDropdownContent} from './TemplatesChatDropdownContent';
import {TypingFlare} from './TypingFlare';

const messageFormSchema = z.object({
  message: sendEventAsPublicUserBody.shape.content,
});

interface MessageFormProps {
  activeConversation: GetConversationDetailResponse;
  refetchActiveConversation: () => void;
  businessCaseId?: string;
  customerId?: string;
  isChattingDisabled?: boolean;
  whatsAppPhoneNumber?: string | null;
}

export function MessageForm(props: MessageFormProps) {
  const dispatch = useDispatch();
  const [createAgentEvent] = chatAgentApi.useCreateAgentEventMutation();
  const [subscribeToChatConversation] = useSubscribeToChatConversationMutation();
  const [sendWhatsAppContentMessage] = useSendWhatsAppContentMessageMutation();

  const {data: currentUser} = useGetCurrentUserInfoQuery();

  const {isOnlineOfferCreated} = useBcToOnlineOffer(props.businessCaseId ?? '');

  const templateContext: TemplateContextEnum = props.businessCaseId ? 'business-case' : 'customer';
  const {data: templates} = useGetContextTemplateListQuery(
    {
      type: 'chat',
      context: templateContext,
    },
    {
      skip: !props.businessCaseId && !props.customerId,
    }
  );

  const {data: whatsAppConfigData, isLoading: isGetWhatsAppConfigQueryLoading} =
    useGetWhatsAppConfigQuery();
  const {
    data: businessCaseMessageChannelData,
    isLoading: isGetBusinessCaseMessageChannelQueryLoading,
  } = useGetBusinessCaseMessageChannelQuery(
    {businessCaseId: props.businessCaseId ?? ''},
    {skip: isNilOrEmpty(props.businessCaseId)}
  );

  const {data: businessCaseData} = useGetBusinessCaseQuery(
    {businessCaseId: props.businessCaseId ?? ''},
    {skip: isNilOrEmpty(props.businessCaseId)}
  );

  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      recordId: props.businessCaseId ?? '',
      resourceId: EntityResourceIds.businessCase,
    },
    {skip: isNilOrEmpty(props.businessCaseId)}
  );

  const [canEditOnlineChat] = usePermissions({
    permissionKeys: ['editOnlineChat'],
    scopes: {
      editOnlineChat: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
    },
  });

  const defaultMessageChannel = businessCaseMessageChannelData?.messageChannelType ?? 'chat';

  const [renderCustomerTemplate, {isLoading: isRenderCustomerTemplateInProgress}] =
    useRenderCustomerSmsTemplateMutation();
  const [renderBusinessCaseTemplate, {isLoading: isRenderBusinessCaseTemplateInProgress}] =
    useRenderBusinessCaseSmsTemplateMutation();
  const [setBusinessCaseMessageChannel, {isLoading: isSetBusinessCaseMessageChannelLoading}] =
    useSetBusinessCaseMessageChannelMutation();

  const isRenderTemplateInProgress =
    isRenderCustomerTemplateInProgress || isRenderBusinessCaseTemplateInProgress;

  const shouldShowTemplates = Boolean(props.businessCaseId || props.customerId);
  const isWhatsAppChannelSelected = defaultMessageChannel === 'whatsapp';
  const isWhatsAppSendDisabled =
    isWhatsAppChannelSelected &&
    (isNilOrEmpty(props.whatsAppPhoneNumber) ||
      isNilOrEmpty(props.businessCaseId) ||
      isNilOrEmpty(props.customerId));

  const handleSetBusinessCaseMessageChannel = (messageChannelType: MessageChannelTypeEnum) => {
    setBusinessCaseMessageChannel({
      businessCaseId: props.businessCaseId ?? '',
      setMessageChannelRequestBody: {messageChannelType},
    });
  };

  const onSubmit: FormSubmitHandler<MessageFormValues> = async (values, _, resetForm) => {
    if (!values.message.trim().length || !props.activeConversation) {
      return;
    }

    if (!props.activeConversation.users.some((user) => user.externalUserId === currentUser?.id)) {
      await subscribeToChatConversation({conversationId: props.activeConversation.id})
        .unwrap()
        .then(() => props.refetchActiveConversation())
        .catch(handleApiError);
    }

    if (
      isWhatsAppChannelSelected &&
      props.whatsAppPhoneNumber &&
      props.businessCaseId &&
      props.customerId
    ) {
      await sendWhatsAppContentMessage({
        sendWhatsAppContentMessageRequestBody: {
          to: props.whatsAppPhoneNumber,
          messageContent: values.message,
          customerId: props.customerId,
          recordId: props.businessCaseId,
          resourceId: EntityResourceIds.businessCase,
          chatConversationId: props.activeConversation.id,
        },
      })
        .unwrap()
        .then(() => {
          resetForm({message: ''});
          dispatch(chatAgentApi.util.invalidateTags(['ConversationEvents', 'Conversation']));
        })
        .catch(handleApiError);

      return;
    }

    await createAgentEvent({conversationId: props.activeConversation.id, content: values.message})
      .unwrap()
      .then(() => resetForm({message: ''}))
      .catch(handleApiError);
  };

  return (
    <Form<MessageFormValues>
      defaultValues={{message: ''}}
      mode="onChange"
      onSubmit={onSubmit}
      experimentalZodSchema={messageFormSchema}
    >
      {(control, formApi) => (
        <DataStatus
          isLoading={isGetWhatsAppConfigQueryLoading || isGetBusinessCaseMessageChannelQueryLoading}
        >
          <HStack width="100%" spacing={2} align="flex-end">
            <TypingFlare formApi={formApi} conversationId={props.activeConversation?.id} />
            <VStack width="100%" spacing={4}>
              <Show when={props.isChattingDisabled}>
                <Text size="xSmall" color="tertiary">
                  {i18n.t('entity.activityPanel.chat.offerUnavailable')}
                </Text>
              </Show>

              <Hide when={props.isChattingDisabled}>
                <VStack spacing={1}>
                  <HStack align="center" justify="space-between" width="100%">
                    <Label>{i18n.t('general.labels.message')}</Label>
                    <Show when={shouldShowTemplates}>
                      <Button
                        variant="link"
                        leftIcon="content/add_circle"
                        title={i18n.t('general.labels.templates')}
                        size="small"
                        onClick={(e) =>
                          openDropdown(
                            e,
                            <TemplatesChatDropdownContent
                              formApi={formApi}
                              control={control}
                              renderCustomerTemplate={renderCustomerTemplate}
                              renderBusinessTemplate={renderBusinessCaseTemplate}
                              templates={templates}
                              customerId={props.customerId}
                              businessCaseId={props.businessCaseId}
                            />,
                            {
                              isHeightLimited: true,
                            }
                          )
                        }
                      />
                    </Show>
                  </HStack>
                  <Box width="100%" position="relative">
                    <Show when={isRenderTemplateInProgress && shouldShowTemplates}>
                      <Spinner variant="overlay" />
                    </Show>
                    <RichContentInput
                      placeholder="Enter message..."
                      name="message"
                      ContentEditableComponent={
                        <Input isDisabled={!canEditOnlineChat || !isOnlineOfferCreated} />
                      }
                      isDisabled={!canEditOnlineChat || !isOnlineOfferCreated}
                    />
                    <Show
                      when={
                        formApi.formState.errors.message && formApi.getFieldState('message').isDirty
                      }
                    >
                      <HelperText errorMessage={formApi.formState.errors.message?.message} />
                    </Show>
                  </Box>
                </VStack>

                <Grid columns={whatsAppConfigData?.enabled ? 2 : 1} spacing={3}>
                  <Show when={whatsAppConfigData?.enabled}>
                    <Dropdown
                      isDisabled={isSetBusinessCaseMessageChannelLoading}
                      dropdownControl={
                        <Button
                          isFullWidth
                          variant="secondary"
                          title={i18n.t(
                            `entity.activityPanel.chat.options.${defaultMessageChannel}`
                          )}
                          rightIcon="navigation/arrow_drop_down"
                        />
                      }
                    >
                      <VStack spacing={1}>
                        <DropdownItem
                          onClick={() => handleSetBusinessCaseMessageChannel('chat')}
                          label={i18n.t('entity.activityPanel.chat.options.chat')}
                        />
                        <DropdownItem
                          onClick={() => handleSetBusinessCaseMessageChannel('whatsapp')}
                          label={i18n.t('entity.activityPanel.chat.options.whatsapp')}
                        />
                      </VStack>
                    </Dropdown>
                  </Show>

                  <Button
                    type="submit"
                    variant="secondary"
                    leftIcon="content/send"
                    title={i18n.t('general.actions.send')}
                    isDisabled={
                      formApi.formState.isSubmitting ||
                      formApi.watch('message').length === 0 ||
                      !formApi.formState.isValid ||
                      isWhatsAppSendDisabled ||
                      !canEditOnlineChat ||
                      !isOnlineOfferCreated
                    }
                  />
                </Grid>
              </Hide>
            </VStack>
          </HStack>
        </DataStatus>
      )}
    </Form>
  );
}
